<script>
  // DRAG_WORDS: the question and text with empty slots, and a bank of word
  // cards to put into them. Two ways to place a card, both always on:
  //  - drag it (mouse, pen or finger) onto a slot, or from a slot onto
  //    another slot (the two swap) or back onto the bank;
  //  - tap it to pick it up, then tap a slot. Tapping a filled slot sends its
  //    card back. The same works with the keyboard (Tab + Enter).
  // Slots are injected via {@html} (they sit mid-sentence in authored
  // markup), so every event is handled by delegation on the root.
  import { onDestroy, tick } from "svelte";
  import { _ } from "svelte-i18n";
  import { joinBlankPartsKeepingWords } from "../bracketBlanks.mjs";
  import {
    placeWord,
    removeWord,
    renderDragSlot,
    unplacedWordIndexes,
  } from "../dragWords.mjs";

  export let exercise; // parseDragWords result
  export let placements = [];
  // Set while the "please answer all" warning is up: empty slots stand out.
  export let showMissing = false;
  export let disabled = false;

  const DRAG_THRESHOLD = 6; // px a pointer travels before a press becomes a drag
  const SCROLL_EDGE = 70; // px from the viewport edge where a drag scrolls the page

  let root;
  let selectedWord = null; // card picked up by a tap, waiting for a slot
  let drag = null; // { wordIndex, fromSlot, pointerId, pointerType, source, startX, startY, x, y, active }
  let dropTarget = null; // element highlighted under the dragged card
  let suppressClick = false;
  let scrollFrame = null;

  $: words = exercise?.words || [];
  $: slotLabel = (number, word) =>
    word
      ? $_("DRAG_WORDS_SLOT_FILLED_LABEL", { values: { number, word } })
      : $_("DRAG_WORDS_SLOT_EMPTY_LABEL", { values: { number } });
  $: questionHtml = joinBlankPartsKeepingWords(exercise?.fields?.question, (slot) =>
    renderDragSlot(slot, words, placements, slotLabel),
  );
  $: textHtml = joinBlankPartsKeepingWords(exercise?.fields?.text, (slot) =>
    renderDragSlot(slot, words, placements, slotLabel),
  );
  $: hasQuestion = hasVisibleContent(questionHtml);
  $: hasText = hasVisibleContent(textHtml);
  $: bank = unplacedWordIndexes(words, placements);
  // A picked-up card that left the bank (dragged into a slot) is dropped.
  $: if (selectedWord !== null && !bank.includes(selectedWord)) selectedWord = null;
  $: if (disabled) {
    selectedWord = null;
    cancelDrag();
  }

  // Same embeds the reports count as content: an image-only question is
  // still a question.
  const EMBED_TAG_PATTERN = /<(img|audio|video|iframe|svg|table)\b/i;

  function hasVisibleContent(html) {
    if (!html) return false;
    if (/data-dw-slot=/.test(html) || EMBED_TAG_PATTERN.test(html)) return true;
    return (
      String(html)
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, "")
        .trim().length > 0
    );
  }

  // ── Tap / keyboard ──

  async function focusSlot(slotIndex) {
    await tick();
    root?.querySelector(`[data-dw-slot="${slotIndex}"]`)?.focus();
  }

  function tapSlot(slotIndex, viaKeyboard) {
    if (selectedWord !== null) {
      placements = placeWord(placements, selectedWord, slotIndex);
      selectedWord = null;
    } else if (placements[slotIndex] !== null && placements[slotIndex] !== undefined) {
      placements = removeWord(placements, slotIndex);
    } else {
      return;
    }
    // The slot markup is rebuilt with the new card, which drops focus.
    if (viaKeyboard) focusSlot(slotIndex);
  }

  function handleClick(event) {
    if (suppressClick) {
      suppressClick = false;
      return;
    }
    if (disabled) return;
    const target = event.target;
    const slot = target?.closest?.("[data-dw-slot]");
    if (slot && root.contains(slot)) {
      // detail is 0 for a click synthesized by Enter / Space.
      tapSlot(Number.parseInt(slot.dataset.dwSlot, 10), event.detail === 0);
      return;
    }
    const card = target?.closest?.("[data-dw-bank-word]");
    if (card) {
      const wordIndex = Number.parseInt(card.dataset.dwBankWord, 10);
      selectedWord = selectedWord === wordIndex ? null : wordIndex;
      return;
    }
    if (target?.closest?.("[data-dw-bank]")) selectedWord = null;
  }

  function handleKeydown(event) {
    if (event.key !== "Escape") return;
    selectedWord = null;
    if (drag?.active) {
      // The button is still down: once released over the card's own slot,
      // its click would send the card back. Swallow that one click.
      suppressClick = true;
      window.addEventListener("pointerup", () => setTimeout(() => (suppressClick = false), 0), {
        once: true,
      });
    }
    cancelDrag();
  }

  // ── Drag ──
  // Pointer events rather than HTML5 drag and drop, which touch screens
  // do not support. The listeners live on window so a drag survives the slot
  // markup being rebuilt underneath it.

  function handlePointerdown(event) {
    if (disabled || drag) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const card = event.target?.closest?.("[data-dw-bank-word]");
    const slot = card ? null : event.target?.closest?.("[data-dw-slot][data-dw-word]");
    const source = card || slot;
    if (!source || !root.contains(source)) return;

    drag = {
      wordIndex: Number.parseInt(
        card ? card.dataset.dwBankWord : slot.dataset.dwWord,
        10,
      ),
      fromSlot: slot ? Number.parseInt(slot.dataset.dwSlot, 10) : null,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      source,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      active: false,
    };
    window.addEventListener("pointermove", handlePointermove, { passive: false });
    window.addEventListener("pointerup", handlePointerup);
    window.addEventListener("pointercancel", cancelDrag);
  }

  function handlePointermove(event) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!drag.active) {
      const moved = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
      if (moved < DRAG_THRESHOLD) return;
      drag.active = true;
      drag.source.classList.add("dw-dragging");
      selectedWord = null;
    }
    event.preventDefault();
    drag.x = event.clientX;
    drag.y = event.clientY;
    drag = drag;
    setDropTarget(targetAt(drag.x, drag.y));
    if (scrollFrame === null) scrollFrame = requestAnimationFrame(autoScroll);
  }

  function handlePointerup(event) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const { active, wordIndex, fromSlot } = drag;
    const target = active ? targetAt(event.clientX, event.clientY) : null;
    cancelDrag();
    // A press that never moved is a tap, left to the click handler.
    if (!active) return;
    // The mouse fires a click right after this pointerup (in the same task);
    // released over the card's own slot, it would click that slot empty.
    suppressClick = true;
    setTimeout(() => (suppressClick = false), 0);
    if (target?.slot !== undefined) {
      placements = placeWord(placements, wordIndex, target.slot);
    } else if (target?.bank && fromSlot !== null) {
      placements = removeWord(placements, fromSlot);
    }
  }

  function cancelDrag() {
    if (typeof window !== "undefined") {
      window.removeEventListener("pointermove", handlePointermove);
      window.removeEventListener("pointerup", handlePointerup);
      window.removeEventListener("pointercancel", cancelDrag);
    }
    if (scrollFrame !== null) {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = null;
    }
    drag?.source?.classList.remove("dw-dragging");
    setDropTarget(null);
    drag = null;
  }

  // Where a card dropped at (x, y) would land: a slot, the bank, or nowhere.
  // The floating card ignores pointer events, so it never hides the target.
  function targetAt(x, y) {
    const element = document.elementFromPoint(x, y);
    if (!element || !root?.contains(element)) return null;
    const slot = element.closest("[data-dw-slot]");
    if (slot) {
      return { element: slot, slot: Number.parseInt(slot.dataset.dwSlot, 10) };
    }
    const bankElement = element.closest("[data-dw-bank]");
    if (bankElement) return { element: bankElement, bank: true };
    return null;
  }

  function setDropTarget(target) {
    const element = target?.element || null;
    if (element === dropTarget) return;
    dropTarget?.classList.remove("dw-drop-over");
    element?.classList.add("dw-drop-over");
    dropTarget = element;
  }

  // Dragging to the top or bottom edge scrolls, since the page cannot be
  // scrolled by touch while a finger is carrying a card.
  function autoScroll() {
    scrollFrame = null;
    if (!drag?.active) return;
    let step = 0;
    if (drag.y < SCROLL_EDGE) {
      step = -Math.ceil((SCROLL_EDGE - drag.y) / 5);
    } else if (drag.y > window.innerHeight - SCROLL_EDGE) {
      step = Math.ceil((drag.y - (window.innerHeight - SCROLL_EDGE)) / 5);
    }
    if (!step) return;
    window.scrollBy(0, step);
    setDropTarget(targetAt(drag.x, drag.y));
    scrollFrame = requestAnimationFrame(autoScroll);
  }

  // The floating card is moved to <body> so no transformed ancestor can
  // offset its fixed position.
  function portal(node) {
    document.body.appendChild(node);
    return {
      destroy() {
        node.remove();
      },
    };
  }

  onDestroy(cancelDrag);
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="dw-exercise"
  class:dw-has-selection={selectedWord !== null}
  class:dw-is-dragging={drag?.active}
  class:dw-show-missing={showMissing}
  class:dw-disabled={disabled}
  bind:this={root}
  on:pointerdown={handlePointerdown}
  on:click={handleClick}
  on:keydown={handleKeydown}
>
  <div class="dw-passage-col">
    {#if hasQuestion}
      <div class="dw-card dw-passage dw-question">{@html questionHtml}</div>
    {/if}
    {#if hasText}
      <div class="dw-card dw-passage">{@html textHtml}</div>
    {/if}
  </div>

  <div class="dw-bank-col">
    <div class="dw-card dw-bank" data-dw-bank>
      <div class="dw-bank-title">{$_("DRAG_WORDS_BANK_TITLE")}</div>
      <div class="dw-bank-words">
        {#each bank as wordIndex (wordIndex)}
          <button
            type="button"
            class="dw-word"
            class:dw-word-selected={selectedWord === wordIndex}
            aria-pressed={selectedWord === wordIndex}
            data-dw-bank-word={wordIndex}>{words[wordIndex]?.text}</button
          >
        {/each}
        {#if bank.length === 0}
          <span class="dw-bank-empty">{$_("DRAG_WORDS_BANK_EMPTY")}</span>
        {/if}
      </div>
      <p class="dw-hint">{$_("DRAG_WORDS_HINT")}</p>
    </div>
  </div>
</div>

{#if drag?.active}
  <div
    use:portal
    class="dw-ghost"
    class:dw-ghost-touch={drag.pointerType !== "mouse"}
    style="left: {drag.x}px; top: {drag.y}px;"
    aria-hidden="true"
  >
    {words[drag.wordIndex]?.text ?? ""}
  </div>
{/if}

<style>
  .dw-exercise {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
    margin-top: 0.5rem;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }

  /* Small screens: the bank first, so the cards are in view before the text. */
  .dw-bank-col {
    order: -1;
  }

  @media (min-width: 992px) {
    .dw-exercise {
      grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
      align-items: start;
    }

    .dw-bank-col {
      order: 0;
    }
  }

  .dw-disabled {
    pointer-events: none;
    opacity: 0.85;
  }

  .dw-card {
    background: #ffffff;
    border: 1px solid #e3edf2;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 1rem 1.1rem;
  }

  .dw-passage {
    line-height: 2.6;
    overflow-wrap: anywhere;
  }

  .dw-passage + .dw-passage {
    margin-top: 0.75rem;
  }

  .dw-passage :global(p:last-child) {
    margin-bottom: 0;
  }

  /* A slot and the punctuation touching it never split across lines. */
  .dw-exercise :global(.blank-word) {
    white-space: nowrap;
  }

  /* ── Slots (injected via @html) ── */
  .dw-exercise :global(.dw-slot) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 6.5rem;
    min-height: 2.1rem;
    margin: 0 0.2rem;
    padding: 0.1rem 0.6rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    color: #1e293b;
    vertical-align: middle;
    background: #f8fafc;
    border: 1.5px dashed #94a3b8;
    border-radius: 8px;
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      background-color 0.12s ease;
  }

  .dw-exercise :global(.dw-slot-filled) {
    background: #e7f6f2;
    border-style: solid;
    border-color: #00a094;
    color: #0f5f58;
    cursor: grab;
    touch-action: none;
  }

  .dw-exercise :global(.dw-slot:focus-visible),
  .dw-word:focus-visible {
    outline: 2px solid #00a094;
    outline-offset: 2px;
  }

  /* A card is picked up or being dragged: every slot invites a drop. */
  .dw-has-selection :global(.dw-slot),
  .dw-is-dragging :global(.dw-slot) {
    border-color: #00a094;
  }

  .dw-exercise :global(.dw-drop-over) {
    background: #ccefe9 !important;
    border-color: #00897f !important;
    box-shadow: 0 0 0 3px rgba(0, 160, 148, 0.2);
  }

  .dw-show-missing :global(.dw-slot:not(.dw-slot-filled)) {
    border-color: #f0b429;
    background: #fffbeb;
  }

  .dw-exercise :global(.dw-dragging) {
    opacity: 0.4;
  }

  /* ── Bank ── */
  .dw-bank-title {
    margin-bottom: 0.6rem;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
  }

  .dw-bank-words {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    min-height: 2.4rem;
  }

  .dw-word {
    padding: 0.35rem 0.8rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    color: #1e293b;
    background: #ffffff;
    border: 1.5px solid #cbd5e1;
    border-bottom-width: 3px;
    border-radius: 8px;
    cursor: grab;
    touch-action: none;
  }

  .dw-word:hover {
    border-color: #00a094;
  }

  .dw-word-selected {
    background: #00a094;
    border-color: #00897f;
    color: #ffffff;
  }

  .dw-bank-empty {
    align-self: center;
    font-size: 0.9rem;
    color: #94a3b8;
  }

  .dw-hint {
    margin: 0.75rem 0 0;
    font-size: 0.8rem;
    color: #64748b;
  }

  /* ── The card following the pointer ── */
  .dw-ghost {
    position: fixed;
    z-index: 2000;
    padding: 0.35rem 0.8rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.4;
    color: #ffffff;
    background: #00a094;
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.25);
    transform: translate(-50%, -50%);
    pointer-events: none;
    white-space: nowrap;
  }

  /* Held above the finger, which would otherwise cover it. */
  .dw-ghost-touch {
    transform: translate(-50%, -140%);
  }
</style>
